import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { CoreConfigService } from '@core/services/config.service';
import { LoginService } from 'app/services/auth/login.service';
import { EnterpriseBrandingService } from 'app/services/enterprise-branding.service';
import { AuthenticationService } from 'app/auth/service';
import { AuthSyncService } from 'app/services/auth/auth-sync.service';
import { getErpJwtToken } from 'app/services/auth/erp-auth-token.util';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-auth-login-v2',
  templateUrl: './auth-login-v2.component.html',
  styleUrls: ['./auth-login-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AuthLoginV2Component implements OnInit, OnDestroy {
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public loading = false;
  public submitted = false;
  public returnUrl: string;
  public error = '';
  public passwordTextType: boolean;
  private _unsubscribeAll: Subject<void>;

  constructor(
    private loginService: LoginService,
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _enterpriseBranding: EnterpriseBrandingService,
    private _authenticationService: AuthenticationService,
    private _authSyncService: AuthSyncService
  ) {
    this._unsubscribeAll = new Subject();
    this._coreConfigService.config = {
      layout: {
        navbar: { hidden: true },
        menu: { hidden: true },
        footer: { hidden: true },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  get f() {
    return this.loginForm.controls;
  }

  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  onSubmit() {
    this.submitted = true;
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.loginService
      .login(this.loginForm.value)
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe({
        next: (data) => {
          const token = data?._token || data?.token;
          if (data?.user && token) {
            this._authenticationService.applyLoginSession(data);
            this._enterpriseBranding.syncFromUser(data.user);
            void this._router.navigate(['/home']);
            return;
          }
          this.error =
            data?.message || data?.error || 'Email ou mot de passe incorrect';
        },
        error: () => {
          this.error = 'Email ou mot de passe incorrect';
        },
      });
  }

  ngOnInit() {
    if (this._authSyncService.syncFromQueryParams()) {
      this._authenticationService.refreshFromLocalStorage();
      const goto = this._authSyncService.consumeGoto();
      void this._router.navigateByUrl(goto || '/dashboards');
      return;
    }

    if (localStorage.getItem('currentUser') && getErpJwtToken()) {
      void this._router.navigateByUrl('/dashboards');
      return;
    }

    // Connexion centralisée sur le hub
    if ((environment as { currentModule?: string }).currentModule !== 'hub') {
      const hub = ((environment as { hubUrl?: string }).hubUrl || 'https://front-hub.agrosilo-solutions.com').replace(/\/$/, '');
      const returnUrl = this._route.snapshot.queryParams['returnUrl'];
      const q = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
      window.location.href = `${hub}/login${q}`;
      return;
    }

    this.loginForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.returnUrl = this._route.snapshot.queryParams['returnUrl'] || '/';
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
